import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { getCurrentUser } from '@/app/apis';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Label } from '@/app/components/ui/label';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, getByteLength } from '@/app/utils';
import { handleServiceError } from '@/app/utils/client';
import { usersUpdateProfile } from '../apis';
import { UserInfoDialog } from './UserInfoDialog';

const UserNameInput = () => {
  const { userInfo, updateUserInfo } = useStore((state) => ({
    userInfo: state.userInfo,
    updateUserInfo: state.updateUserInfo,
  }));

  const t = useTranslations();

  const [isEdit, setIsEdit] = useState(false);
  const [value, setValue] = useState(userInfo?.nickname);
  const [displayError, setDisplayError] = useState<string | boolean>(false);
  const [open, setOpen] = useState(false);

  // 获取用户信息的 useFetch hook
  const { run: getUserInfo } = useFetch(getCurrentUser, {
    manual: true,
    suppressUnauthorizedLoginDialog: true,
    onSuccess: ({ data }) => {
      if (data) {
        // 更新 store 中的用户信息
        updateUserInfo(data);
      }
    },
  });

  const { run: updateUserName } = useFetch(
    () =>
      usersUpdateProfile({
        body: {
          profile: {
            nickname: value,
          },
          mask: 'nickname',
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          handleServiceError(error, t);
          return;
        }
        if (data) {
          toast({
            title: t('personal.username.success'),
          });
          getUserInfo();
          setIsEdit(false);
        }
      },
      onError: () => {
        toast({
          title: t('personal.username.error'),
        });
      },
    },
  );

  const validateUsername = (value: string | undefined) => {
    if (!value) {
      return t('personal.username.requiredError') || 'Username is required';
    }

    if (getByteLength(value) < 4 || getByteLength(value) > 30) {
      return t('personal.username.lengthError');
    }
    // 使用正则表达式验证用户名格式
    // const usernameRegex = /^[a-zA-Z\u4e00-\u9fa5\u0080-\uFFFF0-9_\-'. ]{1,31}$/;
    // if (!usernameRegex.test(value)) {
    //   return (
    //     t('personal.username.formatError')
    //   );
    // }
    return false;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    const error = validateUsername(newValue);
    setDisplayError(error);
  };

  const handleInputBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    const error = validateUsername(newValue);
    setDisplayError(error);
  };

  const handleEdit = () => {
    if (!userInfo?.auditing_info?.nickname) {
      setIsEdit(!isEdit);
    } else {
      toast({
        title: t('personal.bio.auditing'),
      });
    }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <Label className="text-[18px]">{t('personal.username.label')}</Label>
        <div className={cn('flex cursor-pointer items-center gap-1')}>
          <button disabled={!!displayError} type="button" className="font-normal text-[16px] leading-8">
            {isEdit ? (
              <span className={cn(displayError ? 'text-system-white64' : 'text-white')} onClick={() => setOpen(true)}>
                {t('personal.save')}
              </span>
            ) : (
              <span className={cn(!isEdit && 'text-system-blue02')} onClick={() => handleEdit()}>
                {t('personal.edit')}
              </span>
            )}
          </button>
          <CustomTooltip content={t('personal.username.info')} trigger={'hover'} disableHoverableContent={true}>
            <BaseLineExclamation className="shrink-0 text-[16px] text-system-text05" />
          </CustomTooltip>
        </div>
      </div>
      <div className="w-full">
        <input
          type="text"
          disabled={!isEdit}
          className={cn(
            'flex h-10 w-full rounded-8 border border-system-white12 bg-system-bg04 px-3 py-2 text-sm text-system-white64 ring-offset-system-bg06 file:border-0 file:bg-transparent file:font-medium file:text-sm placeholder:text-system-text04 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60',
            displayError && 'border-system-error',
            isEdit && 'text-white',
          )}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          value={value}
          maxLength={31}
          minLength={4}
        />
        {displayError && <p className="mt-1 text-sm text-system-error">{displayError}</p>}
      </div>
      <UserInfoDialog
        title={t('personal.username.dialogTitle')}
        description={t('personal.username.dialogDescription')}
        open={open}
        onChange={() => {
          updateUserName();
        }}
        setOpen={setOpen}
      />
    </>
  );
};

export default UserNameInput;
